import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { queryClient } from "App";
import { normalizeRowError } from "helpers/formErrors";
import CustomTableRowService from "services/CustomTableRow";
import { customTableRowsKey } from "routes/custom_tables/grid/queryKeys";

export { normalizeRowError };

/**
 * Required-field blank-value check used by `CustomTableRowForm`. A boolean
 * `false` is intentionally treated as a present value — only `null`/
 * `undefined`/empty string/empty array count as blank.
 */
export const isBlankValue = (value, type) => {
  if (value == null) return true;
  if (type === "boolean") return false;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

export const useCustomTableRowMutations = slug => {
  const [lastError, setLastError] = useState(null);

  const invalidateRows = () =>
    queryClient.invalidateQueries({ queryKey: customTableRowsKey(slug) });

  const handleError = fallback => err => {
    setLastError(normalizeRowError(err, fallback));
  };

  const createMutation = useMutation({
    mutationFn: data => CustomTableRowService.create(slug, data),
    onSuccess: () => {
      setLastError(null);
      invalidateRows();
    },
    onError: handleError("Failed to create row"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ rowId, data }) =>
      CustomTableRowService.update(slug, rowId, data),
    onSuccess: () => {
      setLastError(null);
      invalidateRows();
    },
    onError: handleError("Failed to save change"),
  });

  const deleteMutation = useMutation({
    mutationFn: rowId => CustomTableRowService.delete(slug, rowId),
    onSuccess: () => {
      setLastError(null);
      invalidateRows();
    },
    onError: handleError("Failed to delete row"),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: ids => CustomTableRowService.bulkDelete(slug, ids),
    onSuccess: () => {
      setLastError(null);
      invalidateRows();
    },
    onError: handleError("Failed to delete rows"),
  });

  return {
    createRow: createMutation.mutateAsync,
    updateCell: updateMutation.mutateAsync,
    deleteRow: deleteMutation.mutateAsync,
    bulkDelete: bulkDeleteMutation.mutateAsync,
    lastError,
    clearLastError: () => setLastError(null),
  };
};
